import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Camera,
    Upload,
    Save,
    Phone,
    User,
    MapPin,
    Calendar,
    AtSign,
    Shield,
    Bell,
    Smartphone,
    Globe,
    Copy,
    Share2,
    LogOut,
    Settings,
    Eye,
    EyeOff,
    Edit3,
    Check,
    X,
    Lock,
    Mail,
    UserCheck,
    Award,
    Star,
    ChevronRight,
    QrCode,
    Download,
    ExternalLink,
    Zap,
    Plus,
    Activity,
    ChevronDown,
    RefreshCcw
} from 'lucide-react';
import { authActions, settingActions } from '../../../redux/actions';
import { formatDate, guessAdminRoute, money, notify } from '../../../utility/Helper1';
import { exchangeSelect, userSelect } from '../../../redux/selectors';
import WrapUpP2p from '../p2p/layouts/WrapUpP2p';
import { useNavigate } from 'react-router-dom';

const Settingsprofile = () => {
    const userData = useSelector(userSelect);
    const dispatch = useDispatch();
    const transactions = useSelector(exchangeSelect);
    const navigate = useNavigate();

    // State management
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [phoneUpdate, setPhoneUpdate] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(userData?.phone || '');
    const [accountUpdate, setAccountUpdate] = useState(false);
    const [editMode, setEditMode] = useState({});
    const [showReferralQR, setShowReferralQR] = useState(false);
    const [profileData, setProfileData] = useState({
        firstname: userData?.firstname || '',
        middlename: userData?.middlename || '',
        surname: userData?.surname || '',
        DOB: userData?.DOB || '',
        sex: userData?.sex || '',
        address: userData?.address || '',
        city: userData?.city || '',
        postalCode: userData?.postalCode || '',
    });




    const doLogOutNowNow = () => {
        dispatch(authActions.onLogout())

        dispatch(settingActions.onManageLocalSettings({ lastPrivateUrl: null }, response => {

        }))
    }

    const calculateTotals = () => {
        const totalInvested = transactions
            .filter(t => t.asset == 1)
            .reduce((sum, t) => sum + t.lastph, 0);

        const totalWithdrawn = transactions
            .filter(t => t.asset == 2)
            .reduce((sum, t) => sum + t.lastgh, 0);

        return { totalInvested, totalWithdrawn };
    };

    const theTotal = calculateTotals()


    const myRefCode = userData?.myrefid
    const referralLink = window.location.origin + "/#/signup?ref=" + myRefCode


    // Handler functions - Using your working Redux logic
    const uploadAvatar = async (file) => {
        if (uploadingAvatar || !file) return;
        setUploadingAvatar(true);

        dispatch(authActions.onLogin("account/change-avatar", { avatar: file }, response => {
            setUploadingAvatar(false);
            if (response?.msg) {
                notify(response.msg, response.code);
            }
        }));
    };

    const updateProfileInfo = async () => {
        if (accountUpdate) return;
        setAccountUpdate(true);

        dispatch(authActions.onLogin("account/update/info", profileData, response => {
            setAccountUpdate(false);
            setEditMode({});
            notify(response?.msg, response?.code);
        }));
    };

    const updatePhoneNumber = async () => {
        if (phoneUpdate) return;
        setPhoneUpdate(true);

        dispatch(authActions.onLogin("account/update/phone", { phone: phoneNumber }, response => {
            setPhoneUpdate(false);
            notify(response?.msg, response?.code);
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const toggleEditMode = (field) => {
        setEditMode(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const copyReferralLink = () => {
        navigator.clipboard.writeText(referralLink);
        notify('Referral link copied to clipboard', 1)
        // Show success notification
    };


    const formatBalance = (amount) => {
        if (!amount) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const capitalizeFirstLetter = (string) => {
        if (!string) return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    return (<WrapUpP2p allowAutoPadding={false}>
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-900 to-slate-900">
            {/* Header */}
            <div className="relative border-b border-slate-800/50 backdrop-blur-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-yellow-500/5 to-amber-600/5"></div>
                <div className="relative max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-light text-white">Profile Settings</h1>
                            <p className="text-slate-400 text-sm mt-1 font-light">
                                Manage account info.
                            </p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>

                            <button
                                onClick={() => navigate('/update/password')}
                                className="flex items-center space-x-2 px-4 py-2.5 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/30 transition-all duration-200"
                            >
                                <RefreshCcw className="h-4 w-4" />
                                <span className="text-sm font-light">Password</span>
                            </button>

                            <button
                                onClick={doLogOutNowNow}
                                className="flex items-center space-x-2 px-4 py-2.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-all duration-200"
                            >
                                <LogOut className="h-4 w-4" />
                                <span className="text-sm font-light">Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Profile Overview */}
                    <div className="space-y-6">
                        {/* Profile Card */}
                        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
                            <div className="relative p-6 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
                                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-yellow-500/10"></div>
                                <div className="relative text-center">
                                    <div className="relative inline-block mb-4">
                                        <div className="w-24 h-24 rounded-full overflow-hidden border-3 border-amber-500/50 shadow-xl">
                                            <img
                                                src={userData?.image ? `${guessAdminRoute()}/${userData.image}` : 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face'}
                                                alt="Profile"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        {uploadingAvatar && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                                                <div className="animate-spin text-white">
                                                    <Camera size={20} />
                                                </div>
                                            </div>
                                        )}
                                        <label
                                            htmlFor="avatar-upload"
                                            className="absolute -bottom-1 -right-1 w-8 h-8 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition cursor-pointer shadow-lg flex items-center justify-center"
                                        >
                                            <Camera size={14} />
                                        </label>
                                        <input
                                            type="file"
                                            id="avatar-upload"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => uploadAvatar(e.target.files[0])}
                                        />
                                    </div>

                                    <h3 className="text-lg font-medium text-white mb-1">{capitalizeFirstLetter(userData?.firstname || '')} {capitalizeFirstLetter(userData?.surname || '')}</h3>
                                    <p className="text-slate-400 text-sm mb-3">@{userData?.username || 'user'}</p>

                                    <div className="flex items-center justify-center space-x-2 mb-4">
                                        <div className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center space-x-1">
                                            <UserCheck className="h-3 w-3 text-emerald-400" />
                                            <span className="text-emerald-300 text-xs font-medium">VERIFIED</span>
                                        </div>

                                        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/update/bank/info')} className="px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full rounded-full flex items-center space-x-1">
                                            <Plus className="h-3 w-3 text-amber-400" />
                                            <span className="text-amber-300 text-xs font-medium">Bank</span>
                                        </div>

                                    </div>

                                    <p className="text-slate-500 text-xs">Member since {formatDate(Number(userData?.date) * 1000)}</p>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                                        <p className="text-sm font-light text-slate-400">Total Invested</p>
                                        <p className="text-white font-medium">{money(theTotal?.totalInvested)}</p>
                                    </div>


                                    <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                                        <p className="text-sm font-light text-slate-400">Total Earnings</p>
                                        <p className="text-emerald-400 font-medium">{money(theTotal?.totalWithdrawn)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Referral Section */}
                        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-light text-white flex items-center">
                                    <Share2 className="h-4 w-4 mr-2 text-amber-400" />
                                    Referral
                                </h3>
                                <button
                                    onClick={() => navigate('/my/referral')}
                                    className="flex items-center space-x-1 text-amber-400 hover:text-amber-300 transition-colors"
                                >
                                    <span className="text-sm">View More</span>
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="p-4 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-600/10 rounded-lg border border-amber-500/20">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-slate-300 text-sm">Your Referral Code</span>
                                    </div>

                                </div>

                                <div className="space-y-2">
                                    <label className="text-slate-400 text-sm">Referral Link</label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={referralLink}
                                            readOnly
                                            className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-slate-300 text-sm font-mono"
                                        />
                                        <button
                                            onClick={copyReferralLink}
                                            className="px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded text-amber-400 hover:bg-amber-500/30 transition-colors text-sm"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Center & Right Columns - Profile Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Personal Information */}
                        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50">
                            <div className="p-6 border-b border-slate-700/50">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-light text-white flex items-center">
                                        <User className="h-5 w-5 mr-3 text-amber-400" />
                                        Personal Information
                                    </h2>
                                    <button
                                        onClick={updateProfileInfo}
                                        disabled={accountUpdate}
                                        className="flex items-center space-x-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 hover:bg-amber-500/30 transition-all duration-200 disabled:opacity-50"
                                    >
                                        {accountUpdate ? (
                                            <>
                                                <div className="animate-spin h-4 w-4 border-2 border-amber-400 border-t-transparent rounded-full"></div>
                                                <span className="text-sm">Updating...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4" />
                                                <span className="text-sm">Save Changes</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* First Name */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light">First Name</label>
                                            <button
                                                onClick={() => toggleEditMode('firstname')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                name="firstname"
                                                value={profileData.firstname}
                                                onChange={handleInputChange}
                                                className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                            />
                                           
                                        </div>

                                    </div>

                                    {/* Middle Name */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light">Middle Name</label>
                                            <button
                                                onClick={() => toggleEditMode('middlename')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                name="middlename"
                                                value={profileData.middlename}
                                                onChange={handleInputChange}
                                                className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                            />
                                          
                                        </div>

                                    </div>

                                    {/* Last Name */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light">Last Name</label>
                                            <button
                                                onClick={() => toggleEditMode('surname')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                name="surname"
                                                value={profileData.surname}
                                                onChange={handleInputChange}
                                                className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                            />
                                           
                                        </div>

                                    </div>

                                    {/* Date of Birth */}
                                    {/* <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light flex items-center">
                                                <Calendar className="h-3 w-3 mr-1" />
                                                Date of Birth
                                            </label>
                                            <button
                                                onClick={() => toggleEditMode('DOB')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        {editMode.DOB ? (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="date"
                                                    name="DOB"
                                                    value={profileData.DOB}
                                                    onChange={handleInputChange}
                                                    className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                                />
                                                <button onClick={() => toggleEditMode('DOB')} className="p-2 bg-emerald-500/20 text-emerald-400 rounded">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-white bg-slate-900/30 px-3 py-2 rounded border border-slate-700/50">
                                                {profileData.DOB ? new Date(profileData.DOB).toLocaleDateString() : 'Not set'}
                                            </p>
                                        )}
                                    </div> */}

                                    {/* Address */}
                                    {/* <div className="md:col-span-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light flex items-center">
                                                <MapPin className="h-3 w-3 mr-1" />
                                                Address
                                            </label>
                                            <button
                                                onClick={() => toggleEditMode('address')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        {editMode.address ? (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    name="address"
                                                    value={profileData.address}
                                                    onChange={handleInputChange}
                                                    className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                                    placeholder="Enter your address"
                                                />
                                                <button onClick={() => toggleEditMode('address')} className="p-2 bg-emerald-500/20 text-emerald-400 rounded">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-white bg-slate-900/30 px-3 py-2 rounded border border-slate-700/50">
                                                {profileData.address || 'Not set'}
                                            </p>
                                        )}
                                    </div>

                                    {/* City & Postal Code */}
                                    {/* <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light">City</label>
                                            <button
                                                onClick={() => toggleEditMode('city')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        {editMode.city ? (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={profileData.city}
                                                    onChange={handleInputChange}
                                                    className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                                />
                                                <button onClick={() => toggleEditMode('city')} className="p-2 bg-emerald-500/20 text-emerald-400 rounded">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-white bg-slate-900/30 px-3 py-2 rounded border border-slate-700/50">
                                                {profileData.city || 'Not set'}
                                            </p>
                                        )}
                                    </div> */}

                                    {/* <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-slate-400 text-sm font-light">Postal Code</label>
                                            <button
                                                onClick={() => toggleEditMode('postalCode')}
                                                className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3 w-3 text-slate-500" />
                                            </button>
                                        </div>
                                        {editMode.postalCode ? (
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="text"
                                                    name="postalCode"
                                                    value={profileData.postalCode}
                                                    onChange={handleInputChange}
                                                    className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                                />
                                                <button onClick={() => toggleEditMode('postalCode')} className="p-2 bg-emerald-500/20 text-emerald-400 rounded">
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-white bg-slate-900/30 px-3 py-2 rounded border border-slate-700/50">
                                                {profileData.postalCode || 'Not set'}
                                            </p>
                                        )}
                                    </div>  */}

                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50">
                            <div className="p-6 border-b border-slate-700/50">
                                <h2 className="text-lg font-light text-white flex items-center">
                                    <Phone className="h-5 w-5 mr-3 text-amber-400" />
                                    Contact Information
                                </h2>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Email (Read-only) */}
                                <div>
                                    <label className="text-slate-400 text-sm font-light mb-2 block flex items-center">
                                        <Mail className="h-3 w-3 mr-1" />
                                        Email Address
                                    </label>
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="email"
                                            value={userData.email}
                                            disabled
                                            className="flex-1 bg-slate-900/30 border border-slate-700/50 rounded px-3 py-2 text-slate-400 cursor-not-allowed"
                                        />
                                        <div className="flex items-center space-x-1 px-3 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded">
                                            <Shield className="h-3 w-3 text-emerald-400" />
                                            <span className="text-emerald-300 text-xs">Verified</span>
                                        </div>
                                    </div>
                                    <p className="text-slate-500 text-xs mt-1">Contact support to change email address</p>
                                </div>

                                {/* Phone Number */}
                                <div>
                                    <label className="text-slate-400 text-sm font-light mb-2 block">Phone Number</label>
                                    <div className="flex space-x-2">
                                        <div className="flex items-center">
                                            <span className="px-3 py-2 bg-slate-900/50 border border-slate-600/50 border-r-0 rounded-l text-slate-300 text-sm">
                                                {userData.countryPhoneCode}
                                            </span>
                                            <input
                                                type="text"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                                className="flex-1 bg-slate-900/50 border border-slate-600/50 rounded-r px-3 py-2 text-white focus:border-amber-500/50 focus:outline-none"
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        <button
                                            onClick={updatePhoneNumber}
                                            disabled={phoneUpdate}
                                            className="px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded text-amber-400 hover:bg-amber-500/30 transition-all duration-200 disabled:opacity-50 text-sm"
                                        >
                                            {phoneUpdate ? (
                                                <div className="animate-spin h-4 w-4 border-2 border-amber-400 border-t-transparent rounded-full"></div>
                                            ) : (
                                                'Update'
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Username (Read-only) */}
                                <div>
                                    <label className="text-slate-400 text-sm font-light mb-2 block flex items-center">
                                        <AtSign className="h-3 w-3 mr-1" />
                                        Username
                                    </label>
                                    <input
                                        type="text"
                                        value={userData.username}
                                        disabled
                                        className="w-full bg-slate-900/30 border border-slate-700/50 rounded px-3 py-2 text-slate-400 cursor-not-allowed"
                                    />
                                    <p className="text-slate-500 text-xs mt-1">Username cannot be changed</p>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>

                {/* Referral QR Modal */}
                {showReferralQR && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-sm w-full">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-white">Referral QR Code</h3>
                                <button
                                    onClick={() => setShowReferralQR(false)}
                                    className="p-1 hover:bg-slate-700 rounded transition-colors"
                                >
                                    <X className="h-4 w-4 text-slate-400" />
                                </button>
                            </div>
                            <div className="bg-white p-4 rounded-lg mb-4">
                                <div className="w-48 h-48 mx-auto bg-gradient-to-br from-slate-200 to-slate-300 rounded-lg flex items-center justify-center">
                                    <QrCode className="h-24 w-24 text-slate-600" />
                                </div>
                            </div>
                            <p className="text-slate-400 text-sm text-center mb-4">
                                Share this QR code with potential referrals
                            </p>
                            <button
                                onClick={() => setShowReferralQR(false)}
                                className="w-full py-2 bg-amber-500/20 border border-amber-500/30 rounded text-amber-400 hover:bg-amber-500/30 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </WrapUpP2p>
    );
};

export default Settingsprofile;